import { uint8ArrayToArrayBuffer } from '../../utils/typed-array';
import { getLocalAuthorityClientInstallId } from './local-authority-vault';

export interface SignedLocalAuthorityEnvelope<Payload extends object> {
  version: 1;
  alg: 'ES256';
  kid: string;
  purpose?: string;
  payload: Payload & {
    issuedAt: string;
    expiresAt: string;
  };
  signature: string;
}

export type LocalAuthorityVerificationStatus =
  | 'valid'
  | 'invalid'
  | 'expired'
  | 'missing-key';

export interface LocalAuthorityVerification<Payload extends object> {
  status: LocalAuthorityVerificationStatus;
  payload: (Payload & { issuedAt: string; expiresAt: string }) | null;
}

export function canonicalStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalStringify).join(',')}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map(key => `${JSON.stringify(key)}:${canonicalStringify(record[key])}`)
    .join(',')}}`;
}

export function localAuthorityRequestHeaders(
  serverBaseUrl: string
): Record<string, string> {
  return {
    'x-streamlain-client-install-id': getLocalAuthorityClientInstallId(),
    'x-streamlain-local-authority-origin': canonicalServerOrigin(serverBaseUrl),
  };
}

export function isSignedLocalAuthorityEnvelope<Payload extends object>(
  value: unknown
): value is SignedLocalAuthorityEnvelope<Payload> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const envelope = value as Record<string, unknown>;
  const payload = envelope.payload;
  return (
    envelope.version === 1 &&
    envelope.alg === 'ES256' &&
    typeof envelope.kid === 'string' &&
    (envelope.purpose === undefined || typeof envelope.purpose === 'string') &&
    typeof envelope.signature === 'string' &&
    payload !== null &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    typeof (payload as Record<string, unknown>).issuedAt === 'string' &&
    typeof (payload as Record<string, unknown>).expiresAt === 'string'
  );
}

export async function verifySignedLocalAuthorityEnvelope<
  Payload extends object,
>(
  envelope: unknown,
  options?: {
    expectedKid?: string;
    expectedPurpose?: string;
    publicKey?: string;
    now?: Date;
  }
): Promise<LocalAuthorityVerification<Payload>> {
  if (!isSignedLocalAuthorityEnvelope<Payload>(envelope)) {
    return { status: 'invalid', payload: null };
  }

  const expectedKid =
    options?.expectedKid ??
    (options?.expectedPurpose === 'permission-grant'
      ? BUILD_CONFIG.permissionGrantKid
      : BUILD_CONFIG.featureEntitlementKid);
  const publicKey =
    options?.publicKey ?? BUILD_CONFIG.featureEntitlementPublicKey;
  if (!expectedKid || !publicKey || !globalThis.crypto?.subtle) {
    return { status: 'missing-key', payload: null };
  }
  if (envelope.kid !== expectedKid) {
    return { status: 'invalid', payload: null };
  }
  if (
    options?.expectedPurpose &&
    envelope.purpose !== options.expectedPurpose
  ) {
    return { status: 'invalid', payload: null };
  }

  let validSignature = false;
  try {
    const cryptoKey = await globalThis.crypto.subtle.importKey(
      'spki',
      uint8ArrayToArrayBuffer(base64ToBytes(publicKey)),
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify']
    );
    validSignature = await globalThis.crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      cryptoKey,
      uint8ArrayToArrayBuffer(base64ToBytes(envelope.signature)),
      uint8ArrayToArrayBuffer(
        new TextEncoder().encode(canonicalStringify(envelope.payload))
      )
    );
  } catch {
    return { status: 'invalid', payload: null };
  }
  if (!validSignature) {
    return { status: 'invalid', payload: null };
  }
  if (
    Date.parse(envelope.payload.expiresAt) <=
    (options?.now ?? new Date()).getTime()
  ) {
    return { status: 'expired', payload: null };
  }
  return { status: 'valid', payload: envelope.payload };
}

function base64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = `${normalized}${'='.repeat((4 - (normalized.length % 4)) % 4)}`;
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function canonicalServerOrigin(value: string): string {
  return new URL(value).origin.toLowerCase();
}
